import { useMatches, useNavigate } from "react-router";

interface BreadcrumbData {
  label: string;
  // Mark if this breadcrumb should be replaced with dynamic data
  dynamic?: boolean;
}

interface BreadcrumbHandle {
  breadcrumb?: (params?: Record<string, string | undefined>) => BreadcrumbData;
}

interface Breadcrumb extends BreadcrumbData {
  pathname: string;
}

interface BreadcrumbsProps {
  // Pass the dynamic label to replace any breadcrumb marked as dynamic
  dynamicLabel?: string;
}

const Breadcrumbs = ({ dynamicLabel }: BreadcrumbsProps = {}) => {
  const matches = useMatches();
  const navigate = useNavigate();

  // Filter matches that have breadcrumb data
  const breadcrumbs = matches
    .filter((match): match is typeof match & { handle: BreadcrumbHandle } => {
      const handle = match.handle as BreadcrumbHandle | undefined;
      return !!handle?.breadcrumb;
    })
    .map((match, index, array): Breadcrumb => {
      const breadcrumbData = match.handle.breadcrumb!(match.params);

      // Replace dynamic breadcrumb labels with the provided dynamic label
      // Only applies to parent breadcrumbs (not the current page)
      if (dynamicLabel && breadcrumbData.dynamic && index < array.length - 1) {
        return {
          ...breadcrumbData,
          label: dynamicLabel,
          pathname: match.pathname,
        };
      }

      return {
        ...breadcrumbData,
        pathname: match.pathname,
      };
    });

  // Only show breadcrumbs if we have at least 2 levels
  if (breadcrumbs.length < 2) {
    return null;
  }

  const currentBreadcrumb = breadcrumbs[breadcrumbs.length - 1];
  const parentBreadcrumbs = breadcrumbs.slice(0, -1);

  return (
    <div className="flex h-6 items-center">
      {parentBreadcrumbs.map((breadcrumb, index) => {
        // If clicking on "Destination Details" from transfer/integrity, go to activity tab (which is now the index)
        const pathname = breadcrumb.pathname;

        return (
          <div key={breadcrumb.pathname} className="flex items-center">
            {index > 0 && <span className="mx-2 text-sm text-gray-400">/</span>}
            <button
              className="
                flex items-center text-sm text-primary-600
                hover:underline
              "
              onClick={() => navigate(pathname)}
            >
              {breadcrumb.label}
            </button>
          </div>
        );
      })}

      <div className="ml-3 flex items-center gap-2">
        <span className="text-sm text-gray-400">/</span>
        <span className="text-sm text-gray-700">{currentBreadcrumb.label}</span>
      </div>
    </div>
  );
};

export default Breadcrumbs;
